import path from "node:path";
import { downloadImage, getFilenameFromUrl, Locator, Response, success, TaskState } from "@iflyrpa/share";
import { Page } from "playwright-core";
import { MockPublish } from "../../types";
import { ProxyAgent } from "../../utils/proxy";
import type { PublishAction } from "./index";

export const rpaAction_Server: PublishAction = async (task, params) => {
    if (params.originalBind && params?.selfDeclaration?.type === "source-statement") {
        return { code: 414, message: "已声明原创不可选择“来源转载”", data: "" };
    }

    const defaultPage = task.steelBrowserContext?.pages()[0];
    if (defaultPage) {
        // 如果 route 已经注册过，就不重复注册
        if (!(defaultPage as any)._routeRegistered) {
            (defaultPage as any)._routeRegistered = true;

            const blockedPatterns = [
                "**/ffmpeg-core.wasm*",
                "**/apm-fe.xiaohongshu.com*",
                "**/t2.xiaohongshu.com*",
                "**/fe-video-qc.xhscdn.com*",
            ].map((pattern) => new RegExp(pattern.replace(/\*\*/g, ".*").replace(/\*/g, "[^/]*")));

            await defaultPage.route("**/*", async (route) => {
                const req = route.request();
                const url = req.url();

                // 是否命中屏蔽规则
                const blocked = blockedPatterns.some((regex) => regex.test(url));
                if (!blocked) {
                    return route.continue();
                }

                if (req.method() === "OPTIONS") {
                    console.log("处理 OPTIONS 预检：", url);
                    await route.fulfill({
                        status: 204,
                        headers: {
                            "access-control-allow-origin": "*",
                            "access-control-allow-methods": "GET,POST,OPTIONS,PUT,DELETE",
                            "access-control-allow-headers": "*",
                        },
                        body: "",
                    });
                    return;
                }

                await route.fulfill({
                    status: 204,
                    body: "",
                });
            });
        }
    }

    const tmpCachePath = task.getTmpPath();
    task.logger?.info("==>进入RPA操作：任务开始,下载图片开始...");
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    const imagePromise = Promise.all(
        params.banners.map((url) => {
            const fileName = getFilenameFromUrl(url);
            return downloadImage(url, path.join(tmpCachePath, fileName));
        }),
    );

    let proxyUrl: string | undefined;
    if (params.proxyLoc) {
        task.logger?.info(`==> 开始获取代理信息：${params}`);
        const ProxyAgentResult = await ProxyAgent({ logger: task.logger }, params.proxyLoc, params.accountId);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = ProxyAgentResult ? `http://${ProxyAgentResult.ip}:${ProxyAgentResult.port}` : undefined;
    }

    task.logger?.info("==>开始打开小红书页面...");
    const page = await task.createPage({
        show: task.debug,
        cookies: params.cookies,
        proxyUrl,
        url: params.url || "https://creator.xiaohongshu.com/publish/publish?source=official&from=menu&target=image",
        ...(params.viewport ? { viewport: params.viewport } : {}),
    });
    task.logger?.info("==>小红书页面打开成功");

    await updateTaskState?.({
        state: TaskState.ACTION,
        connectAddress: task.steelConnector?.getProxyUrl(task.sessionId || "", "v1/sessions/debug"),
        sessionId: task.sessionId,
    });

    const clickTimeout = 1000 * 10;

    // 通用的选择地点
    const selectAddress = async (selector: Locator, address: string) => {
        const instance = typeof selector === "string" ? page.locator(selector) : selector;
        await instance.click({ timeout: clickTimeout });
        await instance.locator("input").fill(address);
        const poperInstance = page.locator('.d-popover:not([style*="display: none"]) .d-options .d-grid-item');
        await poperInstance.first().waitFor();
        await poperInstance.first().click({ timeout: clickTimeout });
    };

    // 通用的选择日期
    const selectDate = async (selector: string | Locator, date: string) => {
        const instance = typeof selector === "string" ? page.locator(selector) : selector;
        await instance.click({ timeout: clickTimeout });
        await instance.fill(date);
        await instance.blur();
    };

    // 查找显示的 popover 弹窗
    const getPoperInstance = async (): Promise<Locator> => {
        await page.waitForTimeout(500);
        const elements = page.locator(".d-popover");
        const count = await elements.count();
        const visibleElements: (typeof elements)[] = [];

        for (let i = 0; i < count; i++) {
            const element = elements.nth(i);
            const style = await element.getAttribute("style");
            if (style && !style.includes("display: none")) {
                visibleElements.push(element);
            }
        }

        if (visibleElements.length > 0) {
            return visibleElements[visibleElements.length - 1];
        }

        throw new Error("未找到 popover 弹窗");
    };

    // 查找显示的 dialog 弹窗
    const getDialogInstance = async (): Promise<Locator> => {
        await page.waitForTimeout(1000);
        const elements = page.locator(".el-dialog");
        return elements.last();
    };

    // 自动化脚本
    // ----------------------------------------------------------------------------------

    // 等待登录成功
    try {
        await page.waitForSelector("#CreatorPlatform", { state: "attached" });
    } catch (error) {
        return { code: 414, message: "登录失效", data: page.url() };
    }
    // 获取待上传图片的本地路径
    const images = await imagePromise;

    task.logger?.info(`==>图片下载完成，共 ${images.length} 张，开始上传图片...`);

    const fileChooser = await page.waitForSelector("input.upload-input", { timeout: 1000 * 10 });

    if (fileChooser) {
        await fileChooser.setInputFiles(images, { timeout: 60 * 1000 }).catch((err) => {
            task.logger?.error(`设置上传文件失败：${err.message}`);
        });
    } else {
        task.logger?.error("文件上传输入框未找到");
    }

    task.logger?.info("==>图片上传完成，开始渲染预览图...");

    // 填写标题
    const titleInstance = page.locator(".input input");
    await titleInstance.waitFor({ state: "attached", timeout: 1000 * 60 });
    await titleInstance.click({ timeout: clickTimeout * 24 });
    task.logger?.info("==> 预览图渲染成功，开始RPA操作！");
    await titleInstance.fill(params.title);

    // 填写正文
    const descInstance = page.locator(".editor-container .tiptap.ProseMirror");
    await descInstance.click({ timeout: clickTimeout });
    await descInstance.fill(`${params.content.replace(/#.*?\[.*?]#/g, "")}\n`);
    await page.keyboard.press("Control+End");

    // 填写话题
    if (params.topic && params.topic.length > 0) {
        for (const it of params.topic) {
            await page.keyboard.insertText(`#${it.name}`);
            await page.locator(".ql-mention-loading").waitFor({ state: "detached" });
            await page.waitForTimeout(1000);
            await page.locator("#creator-editor-topic-container .item.is-selected").click({ timeout: clickTimeout });
            await page.keyboard.press("Control+End");
        }
    }

    // 滚动到底部
    const container = page.locator(".plugin.editor-container");
    await container.focus();
    await page.mouse.wheel(0, 500); // 向下滚动 500 像素

    if (params.address) {
        // 填写地点
        await selectAddress(
            page.locator(".media-extension .address-input").filter({ hasText: "添加地点" }),
            params.address.name,
        );
    }

    // 添加合集
    if (params.collectionBind) {
        await page
            .locator(".media-extension .collection-container")
            .filter({ hasText: "添加合集" })
            .click({ timeout: clickTimeout });
        const collectionName = params.collectionBind.name;
        const collectionBindInstance = await getPoperInstance(); // 获取弹窗实例
        const collectionItemInstance = collectionBindInstance.locator(".d-grid-item .item-label", {
            hasText: collectionName,
        });
        await collectionBindInstance.locator(".loading").waitFor({ state: "detached" });
        await collectionItemInstance.click({ timeout: clickTimeout });
    }

    // 选择群聊
    if (params.groupBind) {
        await page
            .locator(".media-extension .address-input")
            .filter({ hasText: "选择群聊" })
            .click({ timeout: clickTimeout });
        const groupBindName = params.groupBind.group_name;
        const groupBindInstance = await getPoperInstance(); // 获取弹窗实例
        const groupItemInstance = groupBindInstance.locator(".d-grid-item .name", { hasText: groupBindName });
        await groupBindInstance.locator(".loading").waitFor({ state: "detached" });
        await groupItemInstance.click({ timeout: clickTimeout });
    }

    // 原创声明
    const OriginalBindBtn = page.locator(".btn-text.red", { hasText: "去声明" });
    const isOriginalBindBtnVisible = await OriginalBindBtn.isVisible();
    if (isOriginalBindBtnVisible && params.originalBind) {
        await OriginalBindBtn.click({ timeout: clickTimeout });
        const poperInstance = await getPoperInstance();
        await poperInstance.locator(".d-checkbox-simulator").click({ timeout: clickTimeout }); // 勾选同意协议的复选框
        await poperInstance.locator(".footer .custom-button.bg-red").click({ timeout: clickTimeout }); // 点击声明原创按钮
    }

    // 内容类型声明
    if (params.selfDeclaration) {
        // 自主声明
        await page
            .locator(".media-settings .flexbox")
            .filter({ hasText: "内容类型声明" })
            .locator(".d-select-placeholder")
            .click({ timeout: clickTimeout });
        const poperInstance = await getPoperInstance();
        const selfDeclarationInstance = poperInstance.locator(".d-options .d-option");
        if (params.selfDeclaration.type === "fictional-rendition") {
            await selfDeclarationInstance.filter({ hasText: "虚构演绎，仅供娱乐" }).click({ timeout: clickTimeout });
        } else if (params.selfDeclaration.type === "ai-generated") {
            await selfDeclarationInstance.filter({ hasText: "笔记含AI合成内容" }).click({ timeout: clickTimeout });
        } else if (params.selfDeclaration.type === "source-statement") {
            await selfDeclarationInstance.filter({ hasText: /内容来源声明/ }).click({ timeout: clickTimeout });
            // 内容来源声明二级菜单
            const selfDeclarationSecondaryMenuInstance = (await getPoperInstance()).locator(".d-options .d-option");
            // 等待元素出现
            await selfDeclarationSecondaryMenuInstance.first().waitFor();
            if (params.selfDeclaration.childType === "self-labeling") {
                await selfDeclarationSecondaryMenuInstance
                    .filter({ hasText: "已在正文中自主标注" })
                    .click({ timeout: clickTimeout });
            } else if (params.selfDeclaration.childType === "self-shooting") {
                const { shootingDate, shootingLocation } = params.selfDeclaration;
                await selfDeclarationSecondaryMenuInstance
                    .filter({ hasText: "自主拍摄" })
                    .click({ timeout: clickTimeout });
                // 自主拍摄弹窗
                const selfShootingPopup = await getDialogInstance();
                const hasCustomContent = shootingDate || shootingLocation;

                // 选择拍摄地点
                if (shootingLocation) {
                    await selectAddress(selfShootingPopup.locator(".address-input"), shootingLocation.name);
                }
                // 选择拍摄日期
                if (shootingDate) {
                    await selectDate(selfShootingPopup.locator(".date-picker input"), shootingDate);
                }

                await selfShootingPopup
                    .locator("footer button")
                    .filter({ hasText: hasCustomContent ? "确认" : "取消" })
                    .click({ timeout: clickTimeout });
            } else if (params.selfDeclaration.childType === "transshipment") {
                await selfDeclarationSecondaryMenuInstance
                    .filter({ hasText: "来源转载" })
                    .click({ timeout: clickTimeout });
                // 来源媒体弹窗
                const selfShootingPopup = await getDialogInstance();

                const sourceMedia = params.selfDeclaration.sourceMedia;
                if (sourceMedia) {
                    await selfShootingPopup.locator(".el-input input").fill(sourceMedia);
                }

                await selfShootingPopup
                    .locator("footer button")
                    .filter({ hasText: sourceMedia ? "确认" : "取消" })
                    .click({ timeout: clickTimeout });
            }
        }
    }

    // 可见范围 - 仅自己可见
    if (params.visibleRange !== "public") {
        await page
            .locator(".media-settings .flexbox")
            .filter({ hasText: "可见范围" })
            .locator(".d-select-wrapper")
            .click({ timeout: clickTimeout });
        const poperInstance = await getPoperInstance();
        const visibleText = params.visibleRange === "friends" ? "好友可见" : "仅自己可见";
        await poperInstance
            .locator(".d-options .custom-option")
            .filter({ hasText: visibleText })
            .click({ timeout: clickTimeout });
    }

    async function setSwitch(page: Page, label: string, target: boolean) {
        const item = page.locator(".d-new-form-item", {
            has: page.locator(".d-form-item__label", { hasText: label }),
        });

        const switchUI = item.locator(".d-switch-simulator");
        const cls = await switchUI.getAttribute("class");
        const isChecked = cls?.includes("checked") || true;

        if (isChecked !== target) {
            await switchUI.click({ timeout: clickTimeout });
        }
    }
    if (!params.coProduceBind) {
        await setSwitch(page, "允许合拍", false);
    }
    if (!params.noteCopyBind) {
        await setSwitch(page, "允许正文复制", false);
    }

    // task._timerRecord.PrePublish = Date.now();

    const releaseTimeInstance = page
        .locator("label")
        .filter({ hasText: params.isImmediatelyPublish ? "立即发布" : "定时发布" });
    await releaseTimeInstance.click({ timeout: clickTimeout });

    if (params.scheduledPublish) {
        // 自定义发布时间
        await selectDate(".date-picker input", params.scheduledPublish);
    }

    // 等待图片上传完成
    const imgPreviewArea = page.locator(".img-preview-area");
    try {
        await imgPreviewArea.locator(".mask.uploading").waitFor({
            state: "hidden",
            timeout: 50000,
        });
    } catch (error) {
        throw new Error("上传图片超时，请重试！");
    }

    if (MockPublish) {
        const message = "文章模拟发布成功";
        const data = "123456789";

        await updateTaskState?.({
            state: TaskState.SUCCESS,
            result: {
                response: data,
            },
        });
        return success(data, message);
    }

    const response = await new Promise<{ success: boolean; msg: string; data?: { id: string } }>((resolve) => {
        // 定义响应处理函数
        const handleResponse = async (response: Response) => {
            if (response.url().includes("/web_api/sns/v2/note")) {
                const jsonResponse = await response.json();
                page.off("response", handleResponse);
                resolve(jsonResponse);
            }
        };

        // 开始监听响应事件
        page.on("response", handleResponse);

        page.locator(".submit .publishBtn").click({ timeout: clickTimeout });

        // 检查错误toast
        page.waitForSelector(".d-toast-icon-danger", { state: "visible", timeout: 2000 })
            .then(async () => {
                // 等待toast内容完全渲染
                await page.waitForTimeout(300);
                const toastDesc = await page.locator(".d-toast-description").textContent();
                const desc = toastDesc?.trim();
                page.off("response", handleResponse);
                resolve({ success: false, msg: desc || "未知错误" });
            })
            .catch(() => {
                // toast 未出现,继续等待接口响应
            });

        // 添加整体超时保护
        setTimeout(() => {
            page.off("response", handleResponse);
            resolve({ success: false, msg: "发布超时" });
        }, 7000);
    });

    if (!response?.success) {
        const msg = `发布失败：${response?.msg || "未知错误"}`;
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: msg,
        });
        await page.close();
        return { code: 414, message: msg, data: "" };
    }

    await updateTaskState?.({
        state: TaskState.SUCCESS,
        result: {
            response: response?.data?.id || "",
        },
    });

    // 关闭页面
    await page.close();
    return success(response?.data?.id || "");
};
